import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Tabs } from 'antd';

import s from './styles.module.scss';

import type { TabsProps } from 'antd';
import type { IBaseComponentProps } from 'infrastructure/interfaces';

export interface IBaseTab<TKey = string | number> extends IBaseComponentProps {
  key: TKey;
  label: React.ReactNode;
  disabled?: boolean;
  flat?: boolean;
  render?: boolean;
  children?: React.ReactNode;
}

export interface ISegmentedTabsProps<TKey = string | number>
  extends IBaseComponentProps {
  tabs: Array<IBaseTab<TKey>>;
  defaultIndex?: number;
  activeKey?: TKey;
  size?: 'small' | 'default';
  type?: 'rounded' | 'default';
  disabled?: boolean;
  contentClassName?: string;
  onChange?: (tab: IBaseTab<TKey>, index: number) => void;
  maxWidth?: string;
  wrapperClassName?: string;
}

const safetyIndex = (index: number) => (index < 0 ? 0 : index);

const SegmentedTabs = <TKey extends string | number>(
  props: ISegmentedTabsProps<TKey>,
) => {
  const {
    tabs,
    onChange,
    defaultIndex = 0,
    activeKey,
    disabled = false,
    size = 'default',
    type = 'default',
    style,
    className,
    contentClassName,
    wrapperClassName,
    maxWidth,
  } = props;

  const visibleTabs = useMemo(
    () => tabs.filter((t) => t.render !== false),
    [tabs],
  );

  const [selectedTab, setSelectedTab] = useState<IBaseTab<TKey> | undefined>(
    visibleTabs[safetyIndex(defaultIndex)] ?? visibleTabs[0],
  );

  const tabsClassNames = classNames(
    s.segmentedTabs,
    s[size],
    className,
    {
      [s.disabled]: disabled,
    },
    s[type],
  );

  const contentClassNames = classNames(s.content, contentClassName, {
    [s.disabled]: disabled,
    [s.flat]: !!selectedTab?.flat,
  });

  const onTabClick = (tab: IBaseTab<TKey>, index: number) => {
    setSelectedTab(tab);
    onChange?.(tab, index);
  };

  useEffect(() => {
    if (!visibleTabs.length) {
      setSelectedTab(undefined);
      return;
    }

    if (visibleTabs.length === 1) {
      setSelectedTab(visibleTabs[0]);
      return;
    }

    setSelectedTab(visibleTabs[safetyIndex(defaultIndex)] ?? visibleTabs[0]);
  }, [defaultIndex, visibleTabs]);

  useEffect(() => {
    if (activeKey === undefined || activeKey === null) return;

    const tab = visibleTabs.find((t) => t.key === activeKey);
    if (tab) setSelectedTab(tab);
  }, [activeKey, visibleTabs]);

  const items: TabsProps['items'] = useMemo(
    () =>
      visibleTabs.map((tab) => ({
        key: String(tab.key),
        label: tab.label,
        disabled: disabled || tab.disabled,
      })),
    [visibleTabs, disabled],
  );

  const handleChange = (keyStr: string) => {
    const index = visibleTabs.findIndex((t) => String(t.key) === keyStr);
    const tab = visibleTabs[index];
    if (!tab) return;

    onTabClick(tab, index);
  };

  if (!visibleTabs.length) return null;

  return (
    <div className={wrapperClassName}>
      <div style={{ width: maxWidth }} className={s['tabs-scroll-container']}>
        <Tabs
          className={tabsClassNames}
          style={style}
          items={items}
          activeKey={String(selectedTab?.key ?? visibleTabs[0]?.key)}
          onChange={handleChange}
        />
      </div>

      {selectedTab?.children && (
        <div className={contentClassNames}>{selectedTab.children}</div>
      )}
    </div>
  );
};

export default SegmentedTabs;
